// Global audio engine (spec §8): one <audio> element for the whole app, state in zustand.
// - instant start: prefetched signed URL for tonight's story; the element is "unlocked" inside
//   the tap so iOS/Android allow play() after the network round-trip
// - Media Session (lock screen, headphones), resume position, heartbeats
// - stalled/error → automatic retries with a fresh URL, then a clear message
// - sleep timer with a 10 s fade-out
import type { AudioQuality, Lang, PlayResponse } from '@fonus/shared';
import { create } from 'zustand';
import { api, ApiRequestError } from '../api/client.ts';
import { coverPng } from '../lib/cover.ts';
import { createFader } from './fader.ts';
import { flush, track } from './heartbeat.ts';

export interface StoryRef {
  id: string;
  title: string;
  summary: string;
  hue: number;
  durationSec: number | null;
  isTonight: boolean;
}

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'buffering' | 'error';
export type SleepMode = 'off' | '10' | '20' | '30' | 'end';

interface PlayerState {
  story: StoryRef | null;
  lang: Lang;
  status: PlayerStatus;
  position: number;
  duration: number;
  buffered: number;
  sleepMode: SleepMode;
  sleepEndsAt: number | null;
  /** Length of the current timer in seconds (for the night-mode ring). */
  sleepTotalSec: number;
  retrying: boolean;
  /** The story played to the end (player shows the "good night" screen). */
  ended: boolean;
  /** Playback speed: 1, 0.8 or 1.2 (pitch preserved). */
  rate: number;
}

export type PlayOutcome = 'playing' | 'paywall' | 'error';

/** Wiring provided by the app (query cache, paywall, toasts) so the engine stays UI-agnostic. */
export interface EngineHooks {
  onPaywall: () => void;
  onAccessChanged: (res: PlayResponse, storyId: string) => void;
  onCompleted: (storyId: string) => void;
  onError: (message: 'player.error' | 'player.retrying') => void;
  qualityPref: () => AudioQuality;
  onSlowNetwork: () => void;
}

// 0.15 s of silence (ffmpeg anullsrc, 32 kbps mono) used to unlock the element inside a tap.
const SILENT_MP3 =
  'data:audio/mpeg;base64,SUQzBAAAAAAAIlRTU0UAAAAOAAADTGF2ZjYzLjEuMTAyAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTQuMFVVVVVVVVVVVVVVVUxBTUU0LjBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVUxBTUU0LjBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVTEFNRTQuMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//NCxKMAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVTEFNRTQuMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//NAxKQAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVMQU1FNC4wVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVX/80LEowAAA0gAAAAAVVVVVVVVVVVVVVVVVVVVVVVVVVVMQU1FNC4wVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVX/80DEpAAAA0gAAAAAVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsSjAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==';

const MAX_RETRIES = 3;
const HEARTBEAT_MS = 15_000;
const FADE_SEC = 10;
const URL_SAFETY_MS = 5 * 60_000;

const audio = typeof Audio === 'undefined' ? null : new Audio();
// CORS mode: without it the stream is opaque to Web Audio, and on iOS the sleep-timer fade
// (GainNode, see fader.ts) would output silence for the rest of the session. Every audio origin
// must send CORS headers for the webapp origin: R2/Worker (prod), Railway bucket (staging), SeaweedFS.
if (audio) audio.crossOrigin = 'anonymous';
const fader = audio ? createFader(audio) : null;
let hooks: EngineHooks | null = null;
let unlocked = false;
let sessionId: string | null = null;
let sessionStartedAt = 0;
let currentUrlExpiresAt = 0;
let retries = 0;
let heartbeatTimer: ReturnType<typeof setInterval> | undefined;
let sleepTimer: ReturnType<typeof setInterval> | undefined;
let pendingSeek: number | null = null;
let slowNetworkShown = false;

/** Prefetched URLs (POST /stories/:id/prefetch) keyed by story+lang+quality. */
const prefetched = new Map<string, PlayResponse>();
const prefetchKey = (id: string, lang: Lang, q: 'high' | 'low') => `${id}:${lang}:${q}`;

export const usePlayer = create<PlayerState>()(() => ({
  story: null,
  lang: 'uz',
  status: 'idle',
  position: 0,
  duration: 0,
  buffered: 0,
  sleepMode: 'off',
  sleepEndsAt: null,
  sleepTotalSec: 0,
  retrying: false,
  ended: false,
  rate: 1,
}));

const set = usePlayer.setState;
const get = usePlayer.getState;

export function configureEngine(h: EngineHooks): void {
  hooks = h;
}

// ── Quality ──
interface NetworkInformationLike {
  effectiveType?: string;
  saveData?: boolean;
}
export function pickQuality(pref: AudioQuality): 'high' | 'low' {
  if (pref === 'HIGH') return 'high';
  if (pref === 'LOW') return 'low';
  const conn = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  const slow =
    conn?.saveData === true || ['slow-2g', '2g', '3g'].includes(conn?.effectiveType ?? '');
  return slow ? 'low' : 'high';
}

function quality(): 'high' | 'low' {
  const q = pickQuality(hooks?.qualityPref() ?? 'AUTO');
  if (q === 'low' && hooks?.qualityPref() === 'AUTO' && !slowNetworkShown) {
    slowNetworkShown = true;
    hooks.onSlowNetwork();
  }
  return q;
}

// ── Heartbeats ──
function report(completed = false): void {
  const { story, lang, position } = get();
  if (!story || !sessionId) return;
  track({
    sessionId,
    storyId: story.id,
    lang,
    startedAt: new Date(sessionStartedAt).toISOString(),
    progressSec: Math.floor(position),
    completed,
  });
}

function startHeartbeat(): void {
  clearInterval(heartbeatTimer);
  heartbeatTimer = setInterval(() => {
    report();
    void flush();
  }, HEARTBEAT_MS);
}

function stopHeartbeat(): void {
  clearInterval(heartbeatTimer);
  report();
  void flush();
}

// ── Media Session ──
async function updateMediaSession(story: StoryRef): Promise<void> {
  if (!('mediaSession' in navigator)) return;
  const artwork = await coverPng(story.hue);
  navigator.mediaSession.metadata = new MediaMetadata({
    title: story.title,
    artist: 'Fonus Ertak',
    album: 'Fonus Kids',
    ...(artwork && { artwork: [{ src: artwork, sizes: '512x512', type: 'image/png' }] }),
  });
}

function bindMediaSessionHandlers(): void {
  if (!('mediaSession' in navigator)) return;
  const ms = navigator.mediaSession;
  const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
    ['play', () => resume()],
    ['pause', () => pause()],
    ['seekbackward', () => skip(-15)],
    ['seekforward', () => skip(15)],
    ['seekto', (d) => d.seekTime !== undefined && seek(d.seekTime)],
    ['stop', () => pause()],
  ];
  for (const [action, handler] of handlers) {
    try {
      ms.setActionHandler(action, handler);
    } catch {
      // action not supported by this browser
    }
  }
}

function updatePositionState(): void {
  if (!audio || !('mediaSession' in navigator) || !Number.isFinite(audio.duration)) return;
  try {
    navigator.mediaSession.setPositionState({
      duration: audio.duration,
      position: Math.min(audio.currentTime, audio.duration),
      playbackRate: audio.playbackRate,
    });
  } catch {
    // older browsers
  }
}

// ── Element events ──
if (audio) {
  audio.preload = 'metadata';
  audio.addEventListener('loadedmetadata', () => {
    if (pendingSeek !== null && pendingSeek > 0 && pendingSeek < audio.duration - 5) {
      audio.currentTime = pendingSeek;
    }
    pendingSeek = null;
    set({ duration: audio.duration });
    updatePositionState();
  });
  audio.addEventListener('durationchange', () => {
    if (Number.isFinite(audio.duration)) set({ duration: audio.duration });
  });
  audio.addEventListener('timeupdate', () => {
    set({ position: audio.currentTime });
    tickSleep();
  });
  audio.addEventListener('progress', () => {
    const b = audio.buffered;
    if (b.length > 0) set({ buffered: b.end(b.length - 1) });
  });
  audio.addEventListener('waiting', () => {
    if (get().status === 'playing') set({ status: 'buffering' });
  });
  audio.addEventListener('stalled', () => {
    if (get().status === 'playing') set({ status: 'buffering' });
  });
  audio.addEventListener('playing', () => {
    retries = 0;
    set({ status: 'playing', retrying: false });
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
    updatePositionState();
    startHeartbeat();
  });
  audio.addEventListener('pause', () => {
    if (get().status === 'error') return;
    set({ status: 'paused' });
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
    stopHeartbeat();
  });
  audio.addEventListener('ended', () => {
    const { story } = get();
    report(true);
    clearInterval(heartbeatTimer);
    void flush();
    set({ status: 'paused', position: 0, ended: true });
    fader?.set(1);
    if (get().sleepMode === 'end') setSleep('off');
    if (story) hooks?.onCompleted(story.id);
  });
  audio.addEventListener('error', () => {
    if (audio.src === SILENT_MP3 || !get().story) return;
    void recover();
  });
  bindMediaSessionHandlers();
  // Last chance to save progress when Telegram hides or closes the WebView.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      report();
      void flush({ keepalive: true });
    }
  });
  window.addEventListener('pagehide', () => {
    report();
    void flush({ keepalive: true });
  });
}

/**
 * Must run synchronously inside the user's tap: lets later, post-network play() calls through
 * the autoplay policy of iOS Safari / Android WebView.
 */
export function unlockAudio(): void {
  if (!audio || unlocked) return;
  unlocked = true;
  if (audio.src) return;
  audio.src = SILENT_MP3;
  audio.play().then(
    () => {
      if (audio.src === SILENT_MP3) audio.pause();
    },
    () => {
      unlocked = false;
    },
  );
}

async function requestUrl(storyId: string, lang: Lang, q: 'high' | 'low'): Promise<PlayResponse> {
  const cached = prefetched.get(prefetchKey(storyId, lang, q));
  if (cached && Date.parse(cached.expiresAt) - Date.now() > URL_SAFETY_MS) {
    prefetched.delete(prefetchKey(storyId, lang, q));
    // The prefetch didn't spend anything; /play still has to record the listen + unlock state.
    void api<PlayResponse>(`/stories/${storyId}/play`, {
      method: 'POST',
      body: { lang, quality: q },
    })
      .then((res) => hooks?.onAccessChanged(res, storyId))
      .catch(() => undefined);
    return cached;
  }
  return api<PlayResponse>(`/stories/${storyId}/play`, {
    method: 'POST',
    body: { lang, quality: q },
  });
}

/** Prefetch tonight's URL on app open (never spends a free story). */
export async function prefetchStory(storyId: string, lang: Lang): Promise<void> {
  const q = pickQuality(hooks?.qualityPref() ?? 'AUTO');
  try {
    const res = await api<PlayResponse | null>(`/stories/${storyId}/prefetch`, {
      method: 'POST',
      body: { lang, quality: q },
    });
    if (!res || !audio) return;
    prefetched.set(prefetchKey(storyId, lang, q), res);
    // Warm the connection + metadata so the first byte arrives instantly on Play.
    if (!get().story) {
      audio.src = res.url;
      audio.preload = 'metadata';
    }
  } catch {
    // prefetch is an optimisation only
  }
}

/** Starts (or resumes) a story. Call from a tap handler; returns what happened. */
export async function playStory(story: StoryRef, lang: Lang): Promise<PlayOutcome> {
  if (!audio) return 'error';
  unlockAudio();
  const current = get();
  if (current.story?.id === story.id && current.lang === lang && current.status !== 'error') {
    resume();
    return 'playing';
  }

  report();
  void flush();
  sessionId = crypto.randomUUID();
  sessionStartedAt = Date.now();
  retries = 0;
  set({
    story,
    lang,
    status: 'loading',
    position: 0,
    duration: story.durationSec ?? 0,
    buffered: 0,
    retrying: false,
    ended: false,
  });
  void updateMediaSession(story);

  let res: PlayResponse;
  try {
    res = await requestUrl(story.id, lang, quality());
  } catch (err) {
    if (err instanceof ApiRequestError && err.code === 'PAYWALL') {
      set({ story: current.story, lang: current.lang, status: current.story ? 'paused' : 'idle' });
      hooks?.onPaywall();
      return 'paywall';
    }
    set({ status: 'error' });
    hooks?.onError('player.error');
    return 'error';
  }

  hooks?.onAccessChanged(res, story.id);
  currentUrlExpiresAt = Date.parse(res.expiresAt);
  pendingSeek = res.resumeFromSec;
  fader?.set(1);
  if (audio.src !== res.url) audio.src = res.url;
  else if (res.resumeFromSec > 0 && audio.readyState > 0) audio.currentTime = res.resumeFromSec;
  try {
    await audio.play();
    return 'playing';
  } catch {
    // Autoplay refused (no gesture): show as paused, the next tap plays.
    set({ status: 'paused' });
    return 'playing';
  }
}

async function recover(): Promise<void> {
  const { story, lang, position } = get();
  if (!audio || !story) return;
  if (retries >= MAX_RETRIES) {
    set({ status: 'error', retrying: false });
    hooks?.onError('player.error');
    return;
  }
  retries += 1;
  set({ status: 'buffering', retrying: true });
  if (retries === 1) hooks?.onError('player.retrying');
  await new Promise((r) => setTimeout(r, 1000 * 2 ** (retries - 1)));
  try {
    // The signed URL may have expired (6 h) or the CDN edge failed: ask for a fresh one.
    const res = await api<PlayResponse>(`/stories/${story.id}/play`, {
      method: 'POST',
      body: { lang, quality: quality() },
    });
    currentUrlExpiresAt = Date.parse(res.expiresAt);
    pendingSeek = position;
    audio.src = res.url;
    await audio.play();
  } catch {
    void recover();
  }
}

export function pause(): void {
  audio?.pause();
}

export function resume(): void {
  if (!audio || !get().story) return;
  if (Date.now() > currentUrlExpiresAt) {
    void recover();
    return;
  }
  if (get().status === 'error') {
    retries = 0;
    void recover();
    return;
  }
  void audio.play().catch(() => {
    set({ status: 'paused' });
  });
}

export function toggle(): void {
  const { status } = get();
  if (status === 'playing' || status === 'buffering' || status === 'loading') pause();
  else resume();
}

/** Exact playback time for frame-accurate UI (read-along); the store's position is ~4 Hz. */
export function audioTime(): number {
  return audio?.currentTime ?? 0;
}

export function seek(sec: number): void {
  if (!audio) return;
  const max = Number.isFinite(audio.duration) ? audio.duration : get().duration;
  audio.currentTime = Math.max(0, Math.min(sec, Math.max(0, max - 0.5)));
  set({ position: audio.currentTime });
  updatePositionState();
}

export function skip(delta: number): void {
  if (audio) seek(audio.currentTime + delta);
}

// ── Sleep timer ──
export function setSleep(mode: SleepMode): void {
  clearInterval(sleepTimer);
  fader?.set(1);
  const minutes = mode === 'off' || mode === 'end' ? null : Number(mode);
  const { duration, position } = get();
  set({
    sleepMode: mode,
    sleepEndsAt: minutes ? Date.now() + minutes * 60_000 : null,
    sleepTotalSec: minutes ? minutes * 60 : mode === 'end' ? Math.max(0, duration - position) : 0,
  });
  // timeupdate stops while paused, so keep a 1 s tick for the countdown + fade.
  if (mode !== 'off') sleepTimer = setInterval(tickSleep, 1000);
}

/** "+5 minutes" in night mode: extends a running timer (an "until the end" timer becomes timed). */
export function extendSleep(sec: number): void {
  const state = get();
  const remaining = sleepRemaining(state) ?? 0;
  if (state.sleepMode === 'off') return;
  fader?.set(1);
  set({
    sleepMode: state.sleepMode === 'end' ? '30' : state.sleepMode,
    sleepEndsAt: Date.now() + (remaining + sec) * 1000,
    sleepTotalSec: state.sleepTotalSec + sec,
  });
}

/** Playback speed with the voice pitch preserved (0.8× for sleepy kids, 1.2× for re-listening). */
export function setRate(rate: number): void {
  if (audio) {
    // defaultPlaybackRate survives src changes (next story keeps the chosen speed).
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
    audio.preservesPitch = true;
  }
  set({ rate });
  updatePositionState();
}

/** Close the "good night" screen state (e.g. when the player is minimized). */
export function clearEnded(): void {
  set({ ended: false });
}

/** Seconds until the timer stops playback (null = off / until end handled by `ended`). */
export function sleepRemaining(
  state: Pick<PlayerState, 'sleepMode' | 'sleepEndsAt' | 'duration' | 'position'>,
  now = Date.now(),
): number | null {
  if (state.sleepMode === 'off') return null;
  if (state.sleepMode === 'end') return Math.max(0, state.duration - state.position);
  return state.sleepEndsAt === null ? null : Math.max(0, (state.sleepEndsAt - now) / 1000);
}

function tickSleep(): void {
  const state = get();
  const remaining = sleepRemaining(state);
  if (remaining === null || state.status !== 'playing') return;
  if (remaining <= FADE_SEC) fader?.set(remaining / FADE_SEC);
  if (remaining <= 0 && state.sleepMode !== 'end') {
    pause();
    setSleep('off');
  }
}

/** Test hooks. */
export const __test = {
  audio,
  prefetched,
  reset: () => {
    sessionId = null;
    retries = 0;
    slowNetworkShown = false;
    prefetched.clear();
    clearInterval(sleepTimer);
    clearInterval(heartbeatTimer);
    usePlayer.setState({
      story: null,
      status: 'idle',
      position: 0,
      duration: 0,
      buffered: 0,
      sleepMode: 'off',
      sleepEndsAt: null,
      sleepTotalSec: 0,
      retrying: false,
      ended: false,
      rate: 1,
    });
  },
};
